import type { Message } from "@langchain/langgraph-sdk";
import { Fragment } from "react";
import {
  Chat,
  useChatMessages,
  useChatIsStreaming,
  useAgentActivity,
  extractText,
} from "@launch10/use-langgraph-stream";
import type { Artifact } from "@shared";
import { ThinkingIndicator } from "@components/shared/chat/ThinkingIndicator";
import { CompactingIndicator } from "@components/shared/chat/CompactingIndicator";
import { ChatErrorBanner } from "@components/shared/chat/ChatErrorBanner";
import { useAdsChatIsCompacting } from "@components/ads/hooks";
import {
  ArtifactRenderer,
  assignArtifactsToMessages,
  keywordPresent,
  useChatArtifacts,
} from "@components/artifacts";

/**
 * Props for the AdsChatMessagesView presentation component.
 */
export interface AdsChatMessagesViewProps {
  messages: Message[];
  isStreaming: boolean;
  /** True while the agent is compacting history — shows CompactingIndicator. */
  isCompacting?: boolean;
  /** Reviewable artifacts from the channel; each renders anchored to its turn. */
  artifacts?: Artifact[];
  onApprove?: (artifact: Artifact) => void;
}

/**
 * Pure presentation component for ads chat messages.
 * Uses Chat compound components for consistent styling.
 *
 * Human messages route through Recommendations.SubmissionMessage — a
 * submitted recommendation panel renders as its structured card (the
 * transcript anchor); everything else falls back to the normal bubble.
 * The recommendation chips row lives in the input area (AdsChat).
 */
export function AdsChatMessagesView({
  messages,
  isStreaming,
  isCompacting = false,
  artifacts,
  onApprove,
}: AdsChatMessagesViewProps) {
  // Anchor each artifact to its first surfacing tool-message (deduped across
  // the whole stream) — same mapping as the brainstorm chat.
  const artifactsByMessage = assignArtifactsToMessages(messages as never, artifacts);
  // Tool-activity bursts keyed by anchor message id, plus the "anything
  // still running" flag. While a burst is live its card carries its own
  // spinner, so we skip the trailing "Generating" indicator.
  const { byMessage, isLive } = useAgentActivity({ messages, isStreaming });

  // The list itself carries no horizontal padding (see AdsChat's
  // CardContent): bubbles inset themselves with px-4, and artifacts run
  // edge-to-edge of the panel — a keyword table wants every pixel the
  // side chat has.
  // Follow-on AI messages since the last human message continue the
  // same lockup — one conversational pass, not a header per beat.
  let aiHeaderShown = false;

  return (
    <Chat.Messages.List className="flex-1 min-h-0">
      {messages.map((message, index) => {
        const isLastMessage = index === messages.length - 1;

        if (message.type === "human" || (message as any).role === "user") {
          aiHeaderShown = false;
          return (
            <div key={message.id} className="px-4">
              <Chat.Recommendations.SubmissionMessage
                message={message}
                fallback={<Chat.UserMessage message={message} className="text-xs" />}
              />
            </div>
          );
        }

        // A keyword table renders inline at its create_or_edit_artifact
        // tool-call so it stays anchored in history; the table manages its
        // own read-only state once reviewed. Full-bleed: no px wrapper.
        if (message.type === "tool") {
          const anchored = message.id ? artifactsByMessage.get(message.id) : undefined;
          if (!anchored || anchored.length === 0) return null;
          return (
            <div key={message.id} className="px-1.5">
              <ArtifactRenderer
                artifacts={anchored}
                onApprove={onApprove ?? (() => {})}
                disabled={isStreaming}
                present={keywordPresent}
              />
            </div>
          );
        }

        if (message.type === "ai" || (message as any).role === "assistant") {
          const text = extractText(message.content);
          const hasText = text.trim().length > 0;
          const activity = message.id ? byMessage.get(message.id) : undefined;

          if (!hasText && !activity) return null;

          const continuation = aiHeaderShown;
          if (hasText) aiHeaderShown = true;

          return (
            <Fragment key={message.id}>
              {hasText && (
                <div className="px-4">
                  <Chat.AIMessage.Root continuation={continuation}>
                    <Chat.AIMessage.Content
                      content={message.content}
                      showJSON={false}
                      state={isLastMessage ? "active" : "inactive"}
                      className="text-xs"
                    />
                  </Chat.AIMessage.Root>
                </div>
              )}
              {activity && (
                <div className="px-4">
                  <Chat.ToolActivity
                    group={activity}
                    isStreaming={isStreaming}
                    className="text-xs"
                  />
                </div>
              )}
            </Fragment>
          );
        }

        return null;
      })}
      <div className="px-4">
        <Chat.QueuedMessages />
      </div>
      {isCompacting ? (
        <CompactingIndicator className="px-4 text-xs" />
      ) : (
        isStreaming &&
        !isLive && (
          <ThinkingIndicator text="Generating" className="px-4 text-xs" />
        )
      )}
      <div className="px-4">
        <ChatErrorBanner />
      </div>
    </Chat.Messages.List>
  );
}

/**
 * Container component for ads chat messages.
 * Uses Chat context from use-langgraph-stream.
 */
export default function AdsChatMessages() {
  const messages = useChatMessages();
  const isStreaming = useChatIsStreaming();
  const isCompacting = useAdsChatIsCompacting();
  // Approve folds the reviewed table back through the artifacts reducer
  // and triggers the ads keyword-review reminder (persist via
  // update_campaign_assets).
  const { artifacts, onApprove } = useChatArtifacts();

  return (
    <AdsChatMessagesView
      messages={messages as Message[]}
      isStreaming={isStreaming}
      isCompacting={isCompacting}
      artifacts={artifacts}
      onApprove={onApprove}
    />
  );
}
